import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { ensureOwnListing } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { LISTING_PAGE_PARAM_TYPE_DRAFT, LISTING_PAGE_PARAM_TYPE_NEW } from '../../util/urlHelpers';
import IconArrowHead from '../IconArrowHead/IconArrowHead';
import { STEPS as BUDGET_STEPS } from '../../forms/EditProjectListingBudgetForm/EditProjectListingBudgetForm';
import {
  sendChatMessage,
  resetChat,
  generateDraft,
  persistChatTurn,
  persistChatBudget,
} from '../../containers/EditProjectListingPage/EditProjectListingPage.duck';

import css from './EditProjectListingChatPanel.css';

const { Money } = sdkTypes;

// The inline budget card no longer asks for projectDuration — the intake agent
// infers it from the conversation and returns it (chat.projectDuration), so we
// drop that step from the in-chat selections. It is still editable later on the
// draft step (which uses the full BUDGET_STEPS).
const CHAT_BUDGET_STEPS = BUDGET_STEPS.filter(s => s.field !== 'projectDuration');

const TITLE_PLACEHOLDER_MAX = 60;
// Placeholder title used to create the draft listing (Sharetribe requires a
// title on creation). The draft form treats this value as empty so the title
// field shows its placeholder instead of this seed.
export const MANUAL_FILL_FALLBACK_TITLE = 'Uusi työpaikkailmoitus';

// Hardcoded chip suggestions — clicking pre-fills the textarea so the user
// can edit before sending. Order mirrors the design wireframe.
const SUGGESTIONS = [
  {
    id: 'marketingCover',
    labelKey: 'EditProjectListingChatPanel.suggestionMarketingCover',
    seedKey: 'EditProjectListingChatPanel.seedMarketingCover',
  },
  {
    id: 'social',
    labelKey: 'EditProjectListingChatPanel.suggestionSocial',
    seedKey: 'EditProjectListingChatPanel.seedSocial',
  },
  {
    id: 'ai',
    labelKey: 'EditProjectListingChatPanel.suggestionAi',
    seedKey: 'EditProjectListingChatPanel.seedAi',
  },
  {
    id: 'content',
    labelKey: 'EditProjectListingChatPanel.suggestionContent',
    seedKey: 'EditProjectListingChatPanel.seedContent',
  },
  {
    id: 'graphic',
    labelKey: 'EditProjectListingChatPanel.suggestionGraphic',
    seedKey: 'EditProjectListingChatPanel.seedGraphic',
  },
];

const SendArrowIcon = () => (
  <svg className={css.roundSendButtonIcon} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M3.5 10h12M11 5l5 5-5 5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AssistantAvatar = () => <div className={css.assistantAvatar}>F</div>;

const UserBubble = ({ text }) => (
  <div className={css.userRow}>
    <div className={css.userBubble}>{text}</div>
  </div>
);

const TypingDots = ({ label }) => (
  <span className={css.typingIndicator} aria-label={label} role="status">
    <span className={css.typingDot}>●</span>
    <span className={css.typingDot}>●</span>
    <span className={css.typingDot}>●</span>
  </span>
);

const AssistantMessage = ({ text, isTyping, isStreaming, typingLabel }) => (
  <div className={css.assistantRow}>
    <AssistantAvatar />
    <div className={classNames(css.assistantText, { [css.assistantBubble]: !!text })}>
      {text ? (
        <>
          {text}
          {isStreaming ? (
            <span className={css.streamingCursor} aria-hidden="true">
              ▌
            </span>
          ) : null}
        </>
      ) : isTyping ? (
        <TypingDots label={typingLabel} />
      ) : null}
    </div>
  </div>
);

export const EditProjectListingChatPanelComponent = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    submitButtonText,
    onSubmit,
    intl,
    chat,
    onSendMessage,
    onResetChat,
    inlineBudget,
    onGenerateDraft,
    onPersistChatTurn,
    onPersistChatBudget,
    history,
    match,
    currentUser,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const privateData = currentListing.attributes.privateData || {};
  const publicData = currentListing.attributes.publicData || {};
  const existingTitle = currentListing.attributes.title;

  const [input, setInput] = useState('');
  // Send button sits centered on a single line and drops to the bottom-right
  // corner once the textarea wraps to multiple lines.
  const [inputMultiline, setInputMultiline] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // Guards the one-time auto-advance to the draft step after the last selection.
  const advancedRef = useRef(false);
  // Tracks the transcript length we last persisted so each settled turn is saved
  // exactly once (re-renders / re-hydration after the URL swap don't re-save).
  const lastPersistedLenRef = useRef(0);
  // Tracks the previous streaming state so we can focus the input only on the
  // moment the agent finishes replying (the true->false transition).
  const wasStreamingRef = useRef(false);

  // In-chat budget selections (only used when inlineBudget). Seed from the
  // listing's publicData so navigating back onto a draft resumes where it left
  // off instead of re-asking. budgetStep === CHAT_BUDGET_STEPS.length means "done".
  const seedBudgetValues = () => ({
    projectUrgency: publicData.projectUrgency,
    priceEstimate: publicData.priceEstimate,
  });
  const seedBudgetStep = vals => {
    const firstMissing = CHAT_BUDGET_STEPS.findIndex(s => !vals[s.field]);
    return firstMissing === -1 ? CHAT_BUDGET_STEPS.length : firstMissing;
  };
  const [budgetValues, setBudgetValues] = useState(seedBudgetValues);
  const [budgetStep, setBudgetStep] = useState(() => seedBudgetStep(seedBudgetValues()));
  // budgetStep is the "frontier" (first unanswered step). budgetView is the
  // question currently shown in the card — it can trail the frontier so the user
  // can navigate back with the arrows to revisit/edit an earlier answer.
  // budgetDir drives the slide-in animation direction on each step change.
  const [budgetView, setBudgetView] = useState(() => seedBudgetStep(seedBudgetValues()));
  const [budgetDir, setBudgetDir] = useState('forward');
  // Lets the user reveal the budget options manually. The agent's "done" signal
  // (a sentinel it must emit) is not always present even when it has clearly
  // finished — this keeps the flow from dead-ending in that case.
  const [forcedBudget, setForcedBudget] = useState(false);
  // True from the final budget pick until navigation to the draft step takes
  // over. Completing the last step flips budgetComplete to true, which would
  // otherwise un-hide the chat input (see pickingBudget) — but the redirect
  // fired by handleContinue is async, so without this the input flashes into
  // view in that gap. Keeping it set holds the picking-budget layout until the
  // route changes; the only visible change after the final pick is the redirect.
  const [finalizing, setFinalizing] = useState(false);
  // Holds the budget card's appearance for a beat after its reveal conditions are
  // met, so the agent's final message lands first and the card then animates in
  // (instead of both popping in on the same render). Gates only the *show* — the
  // card still hides immediately via shouldRevealBudgetCard in the render below.
  const [budgetCardVisible, setBudgetCardVisible] = useState(false);

  // New-listing flow: reveal the budget options once the agent signals it has
  // enough info, or when the user explicitly chooses to proceed.
  const budgetRevealed = inlineBudget && (chat.agent1Done || forcedBudget);
  const budgetComplete = budgetStep >= CHAT_BUDGET_STEPS.length;
  // The card never renders an out-of-range index: once complete (budgetStep ===
  // length) we clamp the view to the last question so it stays reviewable.
  const budgetViewIndex = Math.min(budgetView, CHAT_BUDGET_STEPS.length - 1);
  // The budget-estimate question is phrased differently depending on the
  // projectDuration the intake agent inferred: a one-off "project" asks for the
  // total budget, a "continuous" engagement asks for the monthly budget.
  const inferredProjectDuration = chat.projectDuration || publicData.projectDuration;
  const budgetQuestionTitleId = step => {
    if (step.field === 'priceEstimate') {
      if (inferredProjectDuration === 'project')
        return 'EditProjectListingChatPanel.budgetQuestionProject';
      if (inferredProjectDuration === 'continuous')
        return 'EditProjectListingChatPanel.budgetQuestionContinuous';
    }
    return step.titleId;
  };
  // While the user is picking budget options we hide the text input so the
  // selections — which do not talk to the agent — are the only thing to do.
  // `finalizing` keeps it hidden through the last pick's async redirect too.
  const pickingBudget = (budgetRevealed && !budgetComplete) || finalizing;

  // The card may render once the agent has signalled done (or the user forced the
  // reveal) and nothing is streaming. budgetCardVisible (set on a short delay
  // below) controls when it actually animates in.
  const shouldRevealBudgetCard = budgetRevealed && !chat.isStreaming;

  // Auto-scroll the transcript to the bottom on new content
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [chat.transcript, chat.streamingText, budgetStep, budgetComplete, budgetCardVisible]);

  // Reveal the budget card a beat after its conditions are met. When the reveal
  // coincides with the agent's final message settling (agent1Done flips as
  // streaming ends), this short hold lets the message land first so the card
  // doesn't appear at the same instant. A manual reveal (forcedBudget, with no
  // fresh message) shows immediately.
  useEffect(() => {
    if (!shouldRevealBudgetCard) {
      setBudgetCardVisible(false);
      return;
    }
    const delay = chat.agent1Done && !forcedBudget ? 600 : 0;
    if (delay === 0) {
      setBudgetCardVisible(true);
      return;
    }
    const timer = setTimeout(() => setBudgetCardVisible(true), delay);
    return () => clearTimeout(timer);
  }, [shouldRevealBudgetCard, chat.agent1Done, forcedBudget]);

  // When the agent finishes replying (streaming ends), focus the message input so
  // the user can type the next message without clicking into it first. Fires only
  // on the true->false transition; skips when the input isn't rendered (e.g. while
  // picking budget). preventScroll keeps the transcript's own auto-scroll in charge.
  useEffect(() => {
    const finishedStreaming = wasStreamingRef.current && !chat.isStreaming;
    wasStreamingRef.current = chat.isStreaming;
    if (finishedStreaming && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [chat.isStreaming]);

  const handleSend = e => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || chat.isStreaming) return;
    setInput('');
    onSendMessage(trimmed);
  };

  const derivePlaceholderTitle = source => {
    if (existingTitle && existingTitle.trim()) return existingTitle;
    if (!source) return MANUAL_FILL_FALLBACK_TITLE;
    const trimmed = source.trim();
    const firstSentence = trimmed.split(/[.\n!?]/)[0] || trimmed;
    return (
      firstSentence.slice(0, TITLE_PLACEHOLDER_MAX).trim() ||
      trimmed.slice(0, TITLE_PLACEHOLDER_MAX).trim()
    );
  };

  // The current draft id, once one exists. In the new-listing flow it appears the
  // moment the first turn is persisted (the container resolves it from
  // submittedListingId, then from the URL after the swap below).
  const listingId = currentListing.id || null;
  const listingIdStr = listingId ? listingId.uuid : null;
  const urlType = match ? match.params.type : null;

  // Mid-chat autosave: after each completed turn, persist the conversation to a
  // draft (created on the first turn, updated thereafter) so an unfinished
  // interview can be resumed later from the chat panel. Only in the new-listing
  // flow (inlineBudget); never mid-stream.
  const persistedTranscript =
    (privateData.jobPostingChat && privateData.jobPostingChat.transcript) || [];
  useEffect(() => {
    if (!inlineBudget || chat.isStreaming) return;
    const len = chat.transcript.length;
    // Skip when there's nothing new to save: fewer than one full exchange, already
    // saved in this session, or already matching what's persisted on the listing
    // (e.g. on the first render after reopening a draft).
    if (len < 2 || len === lastPersistedLenRef.current || len === persistedTranscript.length) {
      return;
    }
    lastPersistedLenRef.current = len;
    const firstUserMessage = chat.transcript.find(m => m.role === 'user');
    onPersistChatTurn({
      titleSeed: derivePlaceholderTitle(firstUserMessage && firstUserMessage.text),
      listingId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.transcript, chat.isStreaming, inlineBudget]);

  // Once the draft exists, swap the throwaway "new" URL for the real "draft" URL
  // (same chat tab) so a reload reloads the saved draft instead of resetting the
  // chat. Fires once — afterwards the URL type is "draft" so the guard short-circuits.
  useEffect(() => {
    if (!inlineBudget || !listingIdStr || urlType !== LISTING_PAGE_PARAM_TYPE_NEW || !history) {
      return;
    }
    history.replace(
      createResourceLocatorString(
        'EditProjectListingPage',
        routeConfiguration(),
        { ...match.params, type: LISTING_PAGE_PARAM_TYPE_DRAFT, id: listingIdStr },
        {}
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingIdStr, urlType, inlineBudget]);

  const handlePickBudget = value => {
    const field = CHAT_BUDGET_STEPS[budgetViewIndex].field;
    const nextValues = { ...budgetValues, [field]: value };
    setBudgetValues(nextValues);
    setBudgetDir('forward');

    // Picking on the frontier (the first unanswered step) advances the flow.
    if (budgetView === budgetStep) {
      const nextStep = budgetStep + 1;
      setBudgetStep(nextStep);
      setBudgetView(nextStep);
      // Last selection answered → advance to the draft step automatically, with no
      // button click. Pass the freshly-completed values since state isn't committed
      // yet. The ref keeps this from firing more than once. Completion persists
      // everything (handleContinue), so skip the partial-pick save below.
      if (nextStep >= CHAT_BUDGET_STEPS.length && !advancedRef.current) {
        advancedRef.current = true;
        // Hold the picking-budget layout (no chat input) until the redirect that
        // handleContinue kicks off swaps in the draft step.
        setFinalizing(true);
        handleContinue(nextValues);
        return;
      }
    }
    // Not completing (first of the two picks, or editing an earlier answer via the
    // arrows) → persist the partial selection so leaving mid-budget resumes on the
    // remaining step instead of re-asking.
    onPersistChatBudget({ listingId, budget: nextValues });
  };

  const handleBudgetBack = () => {
    if (budgetViewIndex <= 0) return;
    setBudgetDir('back');
    setBudgetView(budgetViewIndex - 1);
  };

  const handleBudgetForward = () => {
    // Only navigate forward into steps the user has already reached.
    if (budgetViewIndex >= budgetStep || budgetViewIndex >= CHAT_BUDGET_STEPS.length - 1) return;
    setBudgetDir('forward');
    setBudgetView(budgetViewIndex + 1);
  };

  const handleContinue = budgetOverride => {
    const firstUserMessage = chat.transcript.find(m => m.role === 'user');
    const base = {
      title: derivePlaceholderTitle(firstUserMessage && firstUserMessage.text),
      privateData: {
        ...privateData,
        jobPostingChat: {
          transcript: chat.transcript,
          sessionId: chat.sessionId,
          agent1Done: chat.agent1Done,
        },
      },
    };

    if (inlineBudget) {
      const src = budgetOverride || budgetValues;
      const budget = {
        priceEstimate: src.priceEstimate,
        // projectDuration is inferred by the intake agent, not picked inline.
        // Fall back to publicData when resuming a draft the agent already filled.
        projectDuration: chat.projectDuration || publicData.projectDuration,
        projectUrgency: src.projectUrgency,
      };
      // Kick the writer agent immediately so it streams during the listing
      // save + navigation, minimizing the delay before the draft appears.
      onGenerateDraft({ transcript: chat.transcript, budget });
      onSubmit({ ...base, price: new Money(100000, 'EUR'), publicData: { ...budget } });
    } else {
      onSubmit(base);
    }
  };

  const handleManualFill = e => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const base = {
      title: MANUAL_FILL_FALLBACK_TITLE,
      privateData: {
        ...privateData,
        jobPostingChat: {
          transcript: [],
          sessionId: null,
          agent1Done: false,
          manualFill: true,
        },
      },
    };
    // In the new-listing flow the draft has no price yet; seed the same
    // placeholder the AI flow uses so the manually-filled listing is publishable.
    // The draft panel skips generation (manualFill) and shows an empty form.
    onSubmit(inlineBudget ? { ...base, price: new Money(100000, 'EUR') } : base);
  };

  const handleReset = () => {
    if (window.confirm(intl.formatMessage({ id: 'EditProjectListingChatPanel.resetConfirm' }))) {
      onResetChat();
      setBudgetValues({});
      setBudgetStep(0);
      setBudgetView(0);
      setBudgetDir('forward');
      setForcedBudget(false);
      setFinalizing(false);
      advancedRef.current = false;
    }
  };

  const handleSuggestionClick = suggestion => {
    const seed = intl.formatMessage({ id: suggestion.seedKey });
    setInput(seed);
    if (inputRef.current) {
      inputRef.current.focus();
      // Defer so React commits the new value before we position the cursor at the end
      setTimeout(() => {
        if (inputRef.current) {
          const end = seed.length;
          inputRef.current.setSelectionRange(end, end);
        }
      }, 0);
    }
  };

  const errorText =
    chat.error &&
    (chat.error.message || intl.formatMessage({ id: 'EditProjectListingChatPanel.streamFailed' }));

  const isEmpty = chat.transcript.length === 0 && !chat.isStreaming;
  const hasAnyExchange = chat.transcript.length >= 2;

  // Auto-grow the message textarea to fit its content. CSS max-height caps the
  // growth and overflow-y handles longer input. Runs on every value change
  // (typing, suggestion seed, reset-on-send) and when switching between the
  // empty and active forms (inputRef then points at a fresh node).
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    // Detect whether the content spans more than one line by comparing the
    // grown height to a single line's height (line-height + vertical padding).
    // scrollHeight never drops below the CSS min-height, so a single line that
    // is shorter than min-height must not count as multiline.
    const cs = window.getComputedStyle(el);
    const oneLine = Math.max(
      parseFloat(cs.lineHeight) + parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom),
      parseFloat(cs.minHeight) || 0
    );
    setInputMultiline(el.scrollHeight > oneLine + 4);
  }, [input, isEmpty]);

  // Seed the landing input with what the customer said they were looking for at
  // signup (protectedData.lookingForText). Once only — never overwrite text the
  // user has typed, and never re-seed after the first message has been sent.
  const lookingForText = currentUser?.attributes?.profile?.protectedData?.lookingForText;
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !isEmpty || !lookingForText) return;
    seededRef.current = true;
    setInput(current => current || lookingForText);
  }, [lookingForText, isEmpty]);

  // Footer "proceed" button. New-listing flow: before the budget is revealed it
  // reveals it (so the user is never blocked by a missing "done" signal); once
  // the budget is complete it continues to the draft. Edit mode keeps the
  // original behavior (save chat, advance to the budget tab).
  const proceedReveals = inlineBudget && !budgetRevealed;
  const proceedEnabled = proceedReveals
    ? !disabled && !chat.isStreaming && hasAnyExchange
    : inlineBudget
    ? !disabled && !chat.isStreaming && budgetComplete
    : !disabled && !chat.isStreaming && (chat.agent1Done || hasAnyExchange);
  const proceedReady = inlineBudget ? budgetComplete : chat.agent1Done;
  const proceedLabelId =
    inlineBudget && budgetComplete
      ? 'EditProjectListingChatPanel.continueButtonDraft'
      : 'EditProjectListingChatPanel.continueButton';

  if (isEmpty) {
    return (
      <div className={classNames(classes, css.emptyContainer)}>
        <div className={css.emptyCard}>
          <div className={css.emptyInner}>
            <h1 className={css.emptyHeading}>
              <FormattedMessage id="EditProjectListingChatPanel.emptyTitle" />
            </h1>
            <p className={css.emptySubtitle}>
              <FormattedMessage id="EditProjectListingChatPanel.emptySubtitle" />
            </p>

            <form className={css.emptyInputForm} onSubmit={handleSend}>
              <textarea
                ref={inputRef}
                className={css.emptyInput}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'EditProjectListingChatPanel.firstMessagePlaceholder',
                })}
                disabled={chat.isStreaming}
                rows={1}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e);
                  }
                }}
                autoFocus
              />
              <button
                type="submit"
                className={classNames(css.roundSendButton, {
                  [css.roundSendButtonBottom]: inputMultiline,
                })}
                disabled={!input.trim() || chat.isStreaming}
                aria-label={intl.formatMessage({ id: 'EditProjectListingChatPanel.sendButton' })}
              >
                <SendArrowIcon />
              </button>
            </form>

            <div className={css.suggestionChips}>
              {SUGGESTIONS.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={css.suggestionChip}
                  onClick={() => handleSuggestionClick(s)}
                >
                  <FormattedMessage id={s.labelKey} />
                </button>
              ))}
            </div>

            {errorText && <div className={css.error}>{errorText}</div>}

            <button type="button" className={css.manualLink} onClick={handleManualFill}>
              <FormattedMessage id="EditProjectListingChatPanel.manualFillLink" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames(classes, css.activeRoot)}>
      <div className={css.activeHeader}>
        <div className={css.activeHeaderLeft}>
          <div className={css.breadcrumb}>
            <FormattedMessage id="EditProjectListingChatPanel.breadcrumb" />
          </div>
          <h2 className={css.activeHeading}>
            <FormattedMessage id="EditProjectListingChatPanel.title" />
          </h2>
        </div>
        <button type="button" className={css.manualLinkTop} onClick={handleManualFill}>
          <FormattedMessage id="EditProjectListingChatPanel.manualFillLinkShort" />
        </button>
      </div>

      <div className={css.transcript} ref={scrollRef}>
        {chat.transcript.map((m, i) =>
          m.role === 'user' ? (
            <UserBubble key={i} text={m.text} />
          ) : (
            <AssistantMessage
              key={i}
              text={m.text}
              isTyping={false}
              typingLabel={intl.formatMessage({
                id: 'EditProjectListingChatPanel.typingIndicator',
              })}
            />
          )
        )}
        {chat.isStreaming && (
          <AssistantMessage
            text={chat.streamingText}
            isTyping
            isStreaming={!!chat.streamingText}
            typingLabel={intl.formatMessage({
              id: 'EditProjectListingChatPanel.typingIndicator',
            })}
          />
        )}

        {shouldRevealBudgetCard && budgetCardVisible && (
          <div className={css.budgetCard}>
            <div className={css.budgetCardHeader}>
              <span className={css.budgetCardEyebrow}>
                <FormattedMessage id="EditProjectListingChatPanel.budgetCardEyebrow" />
              </span>
              <div className={css.budgetNav}>
                <button
                  type="button"
                  className={css.budgetNavButton}
                  onClick={handleBudgetBack}
                  disabled={budgetViewIndex <= 0}
                  aria-label={intl.formatMessage({ id: 'EditProjectListingChatPanel.stepBack' })}
                >
                  <IconArrowHead rootClassName={css.budgetNavIcon} direction="left" size="small" />
                </button>
                <span className={css.budgetNavCounter}>
                  {budgetViewIndex + 1} / {CHAT_BUDGET_STEPS.length}
                </span>
                <button
                  type="button"
                  className={css.budgetNavButton}
                  onClick={handleBudgetForward}
                  disabled={
                    budgetViewIndex >= budgetStep || budgetViewIndex >= CHAT_BUDGET_STEPS.length - 1
                  }
                  aria-label={intl.formatMessage({ id: 'EditProjectListingChatPanel.stepForward' })}
                >
                  <IconArrowHead rootClassName={css.budgetNavIcon} direction="right" size="small" />
                </button>
              </div>
            </div>

            <div className={css.budgetProgress}>
              <div
                className={css.budgetProgressFill}
                style={{ width: `${((budgetViewIndex + 1) / CHAT_BUDGET_STEPS.length) * 100}%` }}
              />
            </div>

            <div className={css.budgetStepWrapper}>
              <div
                key={CHAT_BUDGET_STEPS[budgetViewIndex].field}
                className={budgetDir === 'back' ? css.budgetStepBack : css.budgetStepForward}
              >
                <h3 className={css.budgetCardQuestion}>
                  {intl.formatMessage({
                    id: budgetQuestionTitleId(CHAT_BUDGET_STEPS[budgetViewIndex]),
                  })}
                </h3>
                <div className={css.budgetOptions}>
                  {CHAT_BUDGET_STEPS[budgetViewIndex].options.map(opt => {
                    const label = opt.labelId ? intl.formatMessage({ id: opt.labelId }) : opt.label;
                    const subtitle = opt.subtitleId
                      ? intl.formatMessage({ id: opt.subtitleId })
                      : null;
                    const isPicked =
                      budgetValues[CHAT_BUDGET_STEPS[budgetViewIndex].field] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={classNames(css.budgetOption, {
                          [css.budgetOptionPicked]: isPicked,
                        })}
                        onClick={() => handlePickBudget(opt.value)}
                      >
                        <span className={css.budgetOptionText}>
                          <span className={css.budgetOptionLabel}>{label}</span>
                          {subtitle ? (
                            <span className={css.budgetOptionSubtitle}>{subtitle}</span>
                          ) : null}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {errorText && <div className={css.error}>{errorText}</div>}

      {chat.agent1Done && !chat.isStreaming && !inlineBudget && (
        <div className={css.doneBanner}>
          <FormattedMessage id="EditProjectListingChatPanel.doneBanner" />
        </div>
      )}

      {!pickingBudget && (
        <form className={css.activeInputForm} onSubmit={handleSend}>
          <textarea
            ref={inputRef}
            className={css.activeInput}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'EditProjectListingChatPanel.inputPlaceholder',
            })}
            disabled={chat.isStreaming}
            rows={1}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
          />
          <button
            type="submit"
            className={classNames(css.roundSendButton, {
              [css.roundSendButtonBottom]: inputMultiline,
            })}
            disabled={!input.trim() || chat.isStreaming}
            aria-label={intl.formatMessage({ id: 'EditProjectListingChatPanel.sendButton' })}
          >
            <SendArrowIcon />
          </button>
        </form>
      )}
    </div>
  );
};

EditProjectListingChatPanelComponent.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
  inlineBudget: false,
};

EditProjectListingChatPanelComponent.propTypes = {
  className: string,
  rootClassName: string,
  listing: object,
  disabled: bool.isRequired,
  onSubmit: func.isRequired,
  submitButtonText: string.isRequired,
  intl: intlShape.isRequired,
  chat: object.isRequired,
  onSendMessage: func.isRequired,
  onResetChat: func.isRequired,
  // When true (new-listing flow), budget selections are collected inline in the
  // chat and the writer agent is kicked off on continue. False in edit mode.
  inlineBudget: bool,
  onGenerateDraft: func.isRequired,
  onPersistChatTurn: func.isRequired,
  onPersistChatBudget: func.isRequired,
  // from withRouter
  history: object.isRequired,
  match: object.isRequired,
  // from connect — null until the current user has loaded
  currentUser: object,
};

const mapStateToProps = state => ({
  chat: state.EditProjectListingPage.jobPostingChat,
  currentUser: state.user.currentUser,
});

const mapDispatchToProps = dispatch => ({
  onSendMessage: text => dispatch(sendChatMessage(text)),
  onResetChat: () => dispatch(resetChat()),
  onGenerateDraft: payload => dispatch(generateDraft(payload)),
  onPersistChatTurn: payload => dispatch(persistChatTurn(payload)),
  onPersistChatBudget: payload => dispatch(persistChatBudget(payload)),
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(EditProjectListingChatPanelComponent);
